import { NgTemplateOutlet } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, inject, OnInit, signal, Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "@core/services/auth.service";
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { sharesChangeTypes } from "@pages/shares/components/shares-page/shares-change-types";
import { ACNPipe } from "@shared/pipes/acnPipe";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { DocumentsService } from "@shared/services/documents.service";
import { ESignApiService } from "@shared/services/e-sign-api.service";
import { MenuService } from "@shared/services/menu.service";
import { SecurityApiService } from "@shared/services/security-api.service";
import { TrustsApiService } from "@shared/services/trusts-api.service";
import { documentStatusToStep } from "@shared/utils/document-status-to-step";
import { getPageTitle } from "@shared/utils/get-page-title";
import { getSharesPagePath } from "@shared/utils/route-path-utils";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { ToastrService } from "ngx-toastr";
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap
} from 'rxjs';
import { Company } from '@models/companies/company';
import { Document } from "@models/documents/document";
import { LodgementDeadline } from "@models/documents/lodgement-deadline";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { SigningStatus } from "@models/annual-statements/annual-statement-enums";
import { AsicStatus } from "@models/documents/asic-status.enum";
import { ConnectionStatusEnum } from "@models/integrations/connection-status.enum";
import { DocumentStatusEnum } from '@models/documents/document-status.enum';
import { DocumentStepEnum } from "@models/documents/document-step.enum";
import { EntityType } from "@models/common/entity-type.enum";
import { GenerateDocumentsStepsEnum } from "@models/documents/generate-documents-steps.enum";
import { HttpErrorCode } from "@models/requests/http-error-code.enum";
import { RelationshipType, RelationshipTypeLabels } from '@models/relationships/relationship-type.enum';
import { ShareholderRelationshipDetails } from "@models/relationships/relationship";
import { CorporateHolderModel, IndividualHolderModel } from '@models/shares/security-registry-record';
import { ChangeDictionaryHelper } from '@models/ui-models/change-dictionary-helper.model';
import { IStep } from "@models/ui-models/step";
import { Trust } from "@models/trusts/trust";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { ClickableStepperComponent } from "@shared/components/commons/clickable-stepper/clickable-stepper.component";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import { PageTitleComponent } from "@shared/components/page-title/page-title.component";
import {
  ProviderConnectionStatus
} from "@shared/components/documents-related/sign-integrations-dropdown/model/provider-connection-status";
import { BaseShareFormComponent } from "../forms/asic-forms/shares/base-share-form/base-share-form.component";
import { BaseStepperFormComponent } from "../forms/asic-forms/stepper-form";
import { isTrustEstablishmentDocumentType } from "../forms/asic-forms/trusts/constants/trust-establishment-texts";
import { CompanyRegistration } from "../forms/models/company";
import { ModalFormsService } from '../forms/services/modal-forms.service';
import { MarkSignedComponent } from '@shared/components/documents-related/mark-signed/mark-signed.component';
import { AsicResponseComponent } from "./components/asic-response/asic-response.component";
import {
  DocumentsChangesPreviewComponent
} from "./components/documents-changes-preview/documents-changes-preview.component";
import { GenerateDocumentsFormComponent } from "./components/generate-documents-form/generate-documents-form.component";
import {
  GenerateDocumentsHeaderComponent
} from "./components/generate-documents-header/generate-documents-header.component";
import {
  GenerateDocumentsSidebarComponent
} from "./components/generate-documents-sidebar/generate-documents-sidebar.component";
import { SignDocumentsComponent } from "./components/sign-documents/sign-documents.component";

export const ChangeGenerateDocumentsSteps: IStep<GenerateDocumentsStepsEnum>[] = [
  { label: 'Initiate Change', step: 0, disabled: false },
  { label: 'Generate Documents', step: 1, disabled: true },
  { label: 'Sign and Lodge Documents', step: 2, disabled: true },
  { label: 'ASIC Response', step: 3, disabled: true },
];

@Component({
  selector: 'app-generate-documents',
  standalone: true,
  imports: [
    GenerateDocumentsHeaderComponent,
    GenerateDocumentsSidebarComponent,
    ClickableStepperComponent,
    DocumentsChangesPreviewComponent,
    GenerateDocumentsFormComponent,
    SignDocumentsComponent,
    AsicResponseComponent,
    NgxSkeletonLoaderModule,
    NgTemplateOutlet,
    PageTitleComponent,
    ButtonComponent,
  ],
  providers: [],
  templateUrl: 'generate-documents.component.html',
  styleUrl: 'generate-documents.component.scss'
})
export class GenerateDocumentsComponent implements OnInit {
  private documentsService = inject(DocumentsService);
  private documentsApiService = inject(DocumentsApiService);

  private companiesApiService = inject(CompaniesApiService);
  private trustsApiService = inject(TrustsApiService);
  private route = inject(ActivatedRoute);
  private menuService = inject(MenuService);
  private modalFormsService = inject(ModalFormsService);
  private securityService = inject(SecurityApiService);
  private authService = inject(AuthService);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private titleService = inject(Title);
  private eSignService = inject(ESignApiService);
  private modalService = inject(NgbModal);
  destroyRef = inject(DestroyRef);

  steps = ChangeGenerateDocumentsSteps;
  currentStepIndex = 0;
  loading = false;
  updatePreviewTrigger = false;
  readonly notBeLodgedFormTypeList = ['c:ra01', 'c:ra04', 'c:106', 'c:211', 'c:280', 'c:281', 'c:2205', 'Dividend Statement', "RC09"];

  document!: Document;
  actualCompany!: Company;
  actualTrustProfile: Trust | null = null;
  trustProfileBeforeChanges: Trust | null = null;
  lodgement: LodgementDeadline | undefined;
  documentId = '';
  DocumentStatusEnum = DocumentStatusEnum;
  DocumentStepEnum = DocumentStepEnum;
  relationshipHashset: Map<string, string> = new Map<string, string>();
  saveAction$ = new Subject<boolean>();
  confirmAction$ = new Subject<boolean>();
  confirmSentESignAction$ = new Subject<boolean>();
  confirmPaperSignAction$ = new Subject<boolean>();
  confirmSendEmailAction$ = new Subject<boolean>();
  confirmLodgeDocumentNowAction$ = new Subject<boolean>();
  confirmNotLodgedFormAction$= new Subject<boolean>();
  disabledHeaderBtn$: Observable<boolean>;
  declarationState = false;
  collapsedStateBtn = false;
  currentProviderConnectionStatus: ProviderConnectionStatus | null = null;
  hasShareIssue = false;
  isCompanyRegistration = false;
  isTrustEstablishment = false;
  isProviderConnectionStatusLoading = signal(false);
  lodgeNowLoading = this.documentsService.lodgeNowLoading;

  constructor() {
    this.disabledHeaderBtn$ = toObservable(this.documentsService.disabledHeaderBtn as Signal<boolean>);
    this.toastr.toastrConfig.positionClass = 'toast-top-right';
  }

  async ngOnInit(): Promise<void> {
    this.menuService.setMenuState(true);
    this.documentId = this.route.snapshot.paramMap.get('id') ?? '';

    this.listenQueryParams();
    this.listenLodgeNowEvent();
    this.listenDisableHeaderBtn();

    if (this.documentId !== null && this.documentId !== undefined) {
      await this.loadDocument(this.documentId);
      if (!this.document) {
        return;
      }
      this.loadESignProvidersStatus();
    }

    this.getRelationshipHashSet();
    this.openTheOnlyDraftDocumentChange();
  }

  listenQueryParams(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params: { step?: string }) => {
        if (!this) return;

        if (params?.step !== undefined && params?.step !== null) {
          this.currentStepIndex = +params.step as DocumentStatusEnum;
        }
      });
  }

  listenLodgeNowEvent(): void {
    this.confirmLodgeDocumentNowAction$
      .pipe(
        filter(Boolean),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.lodgeDocument();
        this.confirmLodgeDocumentNowAction$?.next(false);
      });
  }

  listenDisableHeaderBtn(): void {
    this.disabledHeaderBtn$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(disabledHeaderBtn => {
        const nextStepIndex = this.currentStepIndex + 1;
        if ((nextStepIndex + 1) <= this.steps.length) {
          this.steps[this.currentStepIndex + 1].disabled = disabledHeaderBtn;
        }
      });
  }

    lodgeDocument(): void {
    const docId = this.document.documentId;
    if (!docId) {
      return;
    }

    // If already signed, just emit the docId; otherwise open modal → update date → emit docId
    const prepareLodge$ = this.document.documentSigning?.signingStatus === SigningStatus.Signed
      ? of(docId)
      : this.openMarkSignedModal().pipe(
          switchMap(signatureDate =>
            this.documentsApiService.updateSignDate([docId], signatureDate)
          ),
          map(() => docId)
        );

    prepareLodge$
      .pipe(
        tap(() => this.lodgeNowLoading.set(true)),
        switchMap(id => this.documentsApiService.lodgeDocument([id])),
        tap(() => {
          void this.setLastStep();
          this.toastr.success('Lodged the documents!', 'Success');
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          this.toastr.error('Error lodging the documents', 'Error');
          return EMPTY;
        }),
        finalize(() => this.lodgeNowLoading.set(false)),
      )
      .subscribe();
  }

  private openMarkSignedModal(): Observable<string> {
    const modalRef = this.modalService.open(MarkSignedComponent, { size: 'lg' });
    (modalRef.componentInstance as MarkSignedComponent).earliestLodgeDate = this.document?.earliestChangeDate ? new Date(this.document?.earliestChangeDate) : undefined;

    return (modalRef.componentInstance as MarkSignedComponent).confirm;
  }


  async loadDocument(id: string): Promise<void> {
    try {
      this.loading = true;
      const document = await firstValueFrom(this.documentsApiService.getById(id)
        .pipe(
          map((document) => new Document(document)),
          catchError((error: HttpErrorResponse) => {
            console.warn('[GenerateDocumentComponent] error: ', error);

            if ((error.status as HttpErrorCode) === HttpErrorCode.NOT_FOUND) {
              this.toastr.error(`Document not found. It may have been deleted or is no longer available.`, 'Error!');
            } else {
              this.toastr.error(`${ error.message }`, 'Error!');
              console.error(error);
            }

            void this.router.navigate(['/companies']);
            return of(null);
          })
        ));

      if(document) {
        this.document = document;

        this.isCompanyRegistration = this.document.changes.some((change) => change.$type == CompanyRegistration.$type);
        this.isTrustEstablishment = isTrustEstablishmentDocumentType(document.type);
        this.titleService.setTitle(getPageTitle(document.company.name ? `${document.company.name} - ${document.description}` : document.description));
        this.changeStepNameForNotBeLodgedForm(document.type);
      } else {
        this.documentsService.disabledHeaderBtn.set(true);
        return;
      }

      //TODO duplicated should one should be removed. Check and refactor
      this.lodgement = await this.lodgementDedline(id);

      if(this.document.documentStatus === DocumentStatusEnum.LodgementPending && this.document.asicStatus !== AsicStatus.NotLodged) {
        this.currentStepIndex = this.document.documentStatus;
      } else {
        this.currentStepIndex = documentStatusToStep(this.document.documentStatus);
      }

      const canSeeAllSteps = this.hasFullAccess;
      if(!canSeeAllSteps) {
        this.currentStepIndex = 0;
      }

      if (Document.isDocumentInvoice(this.document)) {
        this.steps = this.steps.filter((step) => step.step == 2);
        this.currentStepIndex = 2;
      }

      this.steps.forEach((step, index) => {
        if(index <= this.currentStepIndex) {
          step.disabled = false;
        }
      });
      this.addStepToQueryParam(this.currentStepIndex);

      if(
        document.documentSigning?.signingStatus === SigningStatus.Sent
        || document.documentSigning?.signingStatus === SigningStatus.Signed
        || document.documentSigning?.signingStatus === SigningStatus.MarkedAsSigned
        || document.documentSigning?.signingStatus === SigningStatus.MarkedAsSent
        || document.documentSigning?.signingStatus === SigningStatus.Rejected
      ) {
        this.steps[DocumentStepEnum.InitiateChange].disabled = true;
        this.steps[DocumentStepEnum.GenerateDocuments].disabled = true;
      }

      await this.loadEntity(this.document);

      this.updatePreviewTrigger = !this.updatePreviewTrigger;

      if (this.isTrustEstablishment || this.actualTrustProfile) {
        this.steps = ChangeGenerateDocumentsSteps.filter((step) => step.step != +DocumentStepEnum.AsicResponse);
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  }

  changeStepNameForNotBeLodgedForm(documentType: string): void {
    if (this.notBeLodgedFormTypeList.includes(documentType) && this.steps[DocumentStepEnum.AsicResponse]) {
      this.steps[DocumentStepEnum.AsicResponse].label = 'Lodgement with ASIC';
      this.steps = [...this.steps];
    } else if (this.steps[DocumentStepEnum.AsicResponse] && this.steps[DocumentStepEnum.AsicResponse].label !== 'ASIC Response') {
      this.steps[DocumentStepEnum.AsicResponse].label = 'ASIC Response';
      this.steps = [...this.steps];
    }

    const generateDocumentsStep = this.steps.find((step) => step.step == +DocumentStepEnum.GenerateDocuments);

    if (generateDocumentsStep) {
      generateDocumentsStep.label = this.isCompanyRegistration ? 'Declaration' : 'Generate Documents';
    }

    this.steps = this.steps.map((step) => step);
  }

  async loadCompany(id: string, applyIncompleteDocuments = true, excludedDocumentId = '') {
    if(!id) { return null; }
    return await firstValueFrom(this.companiesApiService.getById(id, applyIncompleteDocuments, excludedDocumentId));
  }

  async loadTrust(id: string | null, excludedDocumentId = ''): Promise<Trust | null> {
    if (!id) return null
    const trust= await firstValueFrom(this.trustsApiService.getTrustProfile(id, true, excludedDocumentId));

    if (trust) {
      trust.relationships.forEach((relationship) => {
        const name = relationship.individualDataOverride?.fullNameLabel
          ?? (relationship?.entityDataOverride?.entityNumber
            ? `${ relationship?.entityDataOverride?.name } - ${ relationship?.entityDataOverride?.entityNumber }`
            : relationship?.entityDataOverride?.name)
          ?? '';
        const type = RelationshipTypeLabels[relationship.type];
        this.relationshipHashset.set(relationship.relationshipId, `${ name } (${ type })`);

        if (relationship.details?.$type === 'ShareholderRelationshipDetails') {
          const details = relationship.details as ShareholderRelationshipDetails;
          this.relationshipHashset.set(ChangeDictionaryHelper.BENEFICIAL_OWNER + relationship.relationshipId, details.beneficialOwner);
        }
      });
    }

    return trust;
  }

  getRelationshipHashSet() {
    if (this.actualCompany?.officers && this.actualCompany?.securityholders) {
      const collection = [...this.actualCompany.officers, ...this.actualCompany.securityholders];

      const individualsRecord: Record<string, { fullname: string, types: RelationshipType[] }> = {};
      collection.forEach(officer => {
        if (officer.relationshipId === undefined || officer.entityDataOverride) {
          return;
        }
        this.relationshipHashset.set(officer.relationshipId, `${ officer.individualDataOverride?.fullNameLabel } (${ RelationshipTypeLabels[officer.type] })`);

        if (officer.individualId) {
          if (individualsRecord[officer.individualId]) {
            individualsRecord[officer.individualId].types.push(officer.type);
          } else {
            individualsRecord[officer.individualId] = {
              fullname: officer.individualDataOverride?.fullNameLabel ?? '',
              types: [officer.type],
            };
          }
        }
      });

      Object.entries(individualsRecord)
        .forEach(([key, value]) => {
          const types = value.types.map((type) => RelationshipTypeLabels[type]);
          this.relationshipHashset.set(key, `${ value.fullname } (${ types.join(', ') })`);
        });
      this.relationshipHashset.set(ChangeDictionaryHelper.COMPANY_NAME, this.actualCompany.name);
    }
  }

  editDocumentChange(changeIndex: number): void {
    if (this.modalOpened()) return;
    const modalProps = {
      trustProfile: this.trustProfileBeforeChanges,
      hideApplyChangesButton: true,
      redirectAfterSubmit: false,
    };
    const change = this.document.changes[changeIndex];
    const modalRef = this.modalFormsService.openEntityModalWithDocument(this.document, this.actualCompany, change, modalProps);
    const instance = modalRef.componentInstance as BaseStepperFormComponent<any, any>
    instance.documentSubmitter = ((document: Document, saveAndCompleteLater = false) => {
      this.document.changes[changeIndex] = document.changes[0];
      //this.document.documentSigning = null;
      this.document.documentStatus = DocumentStatusEnum.Draft;

      return this.documentsApiService.createOrUpdateDocument.apply(this.documentsApiService, [this.document, saveAndCompleteLater])
        .pipe(tap({
          next: () => { void this.loadDocument(this.documentId); }
        }));
    }).bind(this);

    modalRef.result.then(async (result: EntityChangeData[]) => {
      this.document.changes[changeIndex] = result[0];
      this.document.changes[changeIndex].changeType = change.changeType;

      await this.loadEntity(this.document);
      this.updatePreviewTrigger = !this.updatePreviewTrigger;
    }, () => {
    });
  }

  setCurrentStep(newStepIndex: number): void {
    if (this.hasShareIssue && !(newStepIndex == 0 || newStepIndex == 3)) {
      this.handleShareIssue();
      return;
    }

    if (newStepIndex > this.currentStepIndex && !this.documentsService.disabledHeaderBtn()) {
      if(this.document.documentStatus && (newStepIndex as DocumentStatusEnum) <= this.document.documentStatus) {
        this.steps[newStepIndex].disabled = false;
        this.currentStepIndex = newStepIndex;
      } else {
        this.steps[newStepIndex].disabled = false;
        this.currentStepIndex = newStepIndex;
        this.confirmAction$.next(true);
      }
    }

    if (newStepIndex < this.currentStepIndex) {
      if (
        this.document.documentStatus == DocumentStatusEnum.SignaturePending
        && this.document.documentSigning?.signingStatus == SigningStatus.SentViaEmail
      ) {
        this.openConfirmEmailSignStepBack()
        return;
      }

      this.currentStepIndex = newStepIndex;
    }

    this.addStepToQueryParam(this.currentStepIndex);
  }

  async setNextStep(): Promise<void> {
    //TODO DO we need it to load on each step ???
    this.lodgement = await this.lodgementDedline(this.documentId);

    const nextStepIndex = this.currentStepIndex + 1;

    if (nextStepIndex < this.steps.length) {
      this.steps[nextStepIndex].disabled = false;
      this.currentStepIndex = nextStepIndex;
    }
  }

  editDocument(): void {
    this.currentStepIndex = DocumentStepEnum.InitiateChange;
    this.editDocumentChange(0);
  }

  async setLastStep(): Promise<void> {
    this.currentStepIndex = this.DocumentStepEnum.AsicResponse;
    this.steps[this.currentStepIndex].disabled = false;
    await this.reloadDocument();
  }

  async reloadDocument(): Promise<void> {
    await this.loadDocument(this.documentId);
  }

  refreshDocument(): void {
    this.documentsApiService.getById(this.documentId)
      .pipe(
        tap((doc) => this.document = new Document(doc)),
        catchError(() => {
          this.toastr.error('Failed to refresh document', 'Error');
          return of(null);
        })
      ).subscribe();
  }

  saveAndCompleteLater(): void {
    if (this.handleShareIssue()) return;
    this.saveAction$.next(true);
  }

  confirmChanges(): void {
    if (this.handleShareIssue()) return
    this.confirmAction$.next(true);
  }

  signDocuments(): void {
    if (this.handleShareIssue()) return
    this.confirmSentESignAction$.next(true);
  }

  paperSignDocuments(): void {
    if (this.handleShareIssue()) return
    this.confirmPaperSignAction$.next(true);
  }

  sendEmail(): void {
    if (this.handleShareIssue()) return
    this.confirmSendEmailAction$.next(true);
  }

  lodgeDocumentNow(): void {
    if (this.handleShareIssue()) return
    this.confirmLodgeDocumentNowAction$.next(true);
  }

  confirmNotLodged(): void {
    if (this.handleShareIssue()) return
    this.confirmNotLodgedFormAction$.next(true);
  }

  private openTheOnlyDraftDocumentChange() {
    if (this.document && this.document.changes.length === 1 && this.document.documentStatus === DocumentStatusEnum.Draft) {
      this.editDocumentChange(0);
    }
  }

  private async loadActualCompany(excludedDocumentId: string) {
    try {
      const company = await this.loadCompany(this.document.company?.entityId, true, excludedDocumentId);
      if (company) {
        this.actualCompany = company;
        this.hasShareIssue = BaseShareFormComponent.isCompanyIncludeShareIssue(company);
        this.getRelationshipHashSet();
      } else {
        this.actualCompany = new Company();
      }
    } catch (error) {
      console.warn(error);
    }
  }

  //TODO: Check if this should be used. Should we load company with incomleted documents?
  //We load company with documen. Seems this method should not be call fo each docuemnt loading
  private async loadEntity(document: Document): Promise<void> {
    try {
      const documentTypes = ['CompanyChangePartyName'];
      const documentId = documentTypes.some(t => document.changes.some((change) => {
        return change.$type === t;
      }))
        ? '' :
        document.documentId;

      if (document.type.startsWith('c:') || document.company?.entityId) {
        await this.loadActualCompany(documentId);
        await this.loadSecurities();
      } else if (document.type.startsWith('t:') || document.type == 'discretionary' || document.entityId || this.isTrustEstablishment) {
        this.trustProfileBeforeChanges = await this.loadTrust(document.entityId, document.documentId);
        this.actualTrustProfile = await this.loadTrust(document.entityId);
      } else {
        console.error('Unrecognized document type:', document.type);
        this.toastr.error(`Unrecognized document type: ${document.type}`, 'Error');
      }
    } catch (error) {
      console.error('Unknown Entity To Load:', error);
      this.toastr.error('Unknown Entity To Load', 'Error');
    }
  }

  private async loadSecurities() {
    this.updatePreviewTrigger = !this.updatePreviewTrigger;
    if (!this.document.company?.entityId) {
      return;
    }

    try {
      const records = await firstValueFrom(this.securityService.getSecurityRegistryRecords(this.document.company.entityId));
      records.forEach(record => {
        record.holders.forEach(holder => {
          if (holder.$type === IndividualHolderModel.$type) {
            const holderType = holder.details.$type.replace('RelationshipDetails', '');
            this.relationshipHashset.set(holder.relationshipId, `${holder.name} (${holderType})`);
          } else if (holder.$type === CorporateHolderModel.$type) {
            const corporateHolder = holder as CorporateHolderModel;
            const companyNumber = corporateHolder.entityData.entityNumber ? ` - ${ new ACNPipe().transform(corporateHolder.entityData.entityNumber) }` : '';
            this.relationshipHashset.set(holder.relationshipId, corporateHolder.entityData.name + companyNumber);
          }

          if (holder.details.beneficialOwner) {
            this.relationshipHashset.set(ChangeDictionaryHelper.BENEFICIAL_OWNER + holder.relationshipId, holder.details.beneficialOwner);
          }
        });
      });
    } catch (error) {
      console.error('Error loading securities:', error);
    }
  }

  private addStepToQueryParam(step: DocumentStepEnum): void {
    const currentUrl = this.router.url.split('?')[0];
    void this.router.navigate([currentUrl], {
      queryParams: { step },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  public loadESignProvidersStatus() {
    this.isProviderConnectionStatusLoading.set(true);
    return this.eSignService.getESignProvidersStatus()
      .pipe(finalize(() => this.isProviderConnectionStatusLoading.set(false)))
      .subscribe({
        next: (providerConnectionStatuses) => {
          this.currentProviderConnectionStatus = providerConnectionStatuses.find((providerConnectionStatus) => providerConnectionStatus.status == ConnectionStatusEnum.Connected) ?? null;
        },
        error: (error) => {
          this.toastr.error('Failed load eSign providers', 'Error');
          console.warn('[loadESignProvidersStatus] error: ', error);
          this.currentProviderConnectionStatus = null;
        }
      })
  }

  get entityType(): EntityType {
    if (this.document?.type.startsWith('t:') || isTrustEstablishmentDocumentType(this.document?.type))
      return EntityType.Trust;

    return EntityType.Company;
  }

  get modalOpened() {
    return this.modalFormsService.modalOpened;
  }

  get hasFullAccess() {
    const allowedRoles = ['Admin', 'Manager'];
    const canSeeAllSteps = this.authService.hasRole(allowedRoles);
    return canSeeAllSteps;
  }

  onDeclarationStateChange(state: boolean): void {
    this.declarationState = state;
  }

  get pageTitle(): string {
    return this.entityType == EntityType.Company
      ? 'Document Lodgement Tracker'
      : 'Document Details';
  }

  async lodgementDedline(documentId: string): Promise<LodgementDeadline | undefined> {
    return await firstValueFrom(this.documentsApiService.getLodgementDeadline([documentId]).pipe(
      catchError(() => {
        this.loading = false;
        this.toastr.error(`Lodgement loading error`, 'Error!');
        return of(undefined);
      })
    ));
  }

  handleShareIssue(): boolean {
    const documentChangesTypes = this.document.changes.map((change) => change.changeType);
    const documentChangesIncludeShareType = documentChangesTypes.some((changeType) => sharesChangeTypes.includes(changeType));

    if (!this.hasShareIssue || !documentChangesIncludeShareType) return false;

    const body = `
        <div class="text-center">Share issues have been identified for this company.</div>
        <div class="text-center">Click ‘Resolve’ to review and fix the details.</div>
    `;

    const modalRef = this.modalFormsService.openModal(CommonActionsModalComponent, { size: 'sm', centered: true });
    const instance = modalRef.componentInstance as CommonActionsModalComponent;

    const actions: IActionButtonConfig[] = [
      {
        text: 'Resolve',
        iconClass: 'icon ic-resolve-20',
        action: of(true)
      },
      {
        text: 'Cancel',
        btnColor: 'white',
        action: of(false),
      }
    ];

    instance.config = {
      ...instance.config,
      actions,
      body,
      iconClass: 'ic-alert-triangle-20 bg-red',
      header: 'Share Issues Found',
    };
    instance['forceClose'] = true;

    modalRef.result.then(
      (result) => {
        this.modalFormsService.closeAllFormsModals();

        if (result) {
          void this.router.navigate(getSharesPagePath(this.actualCompany.entityId));
        }
      },
      () => this.modalFormsService.closeAllFormsModals()
    );

    return true;
  }

  openConfirmEmailSignStepBack(): void {
    const modalRef = this.modalFormsService.openModal(CommonActionsModalComponent, { size: 'sm', centered: true });
    const instance = modalRef.componentInstance as CommonActionsModalComponent;
    instance['forceClose'] = true;

    const actions: IActionButtonConfig[] = [
      {
        text: 'Proceed',
        btnColor: 'orange',
        action: of(true).pipe(switchMap(() => this.resetDocumentSendViaEmail(this.document))),
      },
      {
        text: 'Cancel',
        btnColor: 'white',
        action: of(false).pipe(tap(() => instance.close())),
      },
    ];

    const body = `
      <div>
        <div>Moving the document to a previous step will result in the following changes:</div>

        <ul class="my-2">
          <li>Status will be set to "Authorisation Pending"</li>
          <li>Signing status will be cleared</li>
          <li>Any signing reminders will be deleted</li>
        </ul>

        <div>Do you want to proceed?</div>
      </div>
    `;

    instance.config = {
      ...instance.config,
      header: 'Confirm Moving to Previous Step',
      body,
      actions
    }
  }

  resetDocumentSendViaEmail(document: Document): Observable<void> {
    return this.documentsApiService.resetDocumentSendViaEmail(document.documentId)
      .pipe(
        tap(() => {this.toastr.success('Document has been reset successfully', 'Success'); }),
        catchError(() => {
          this.toastr.error('Failed to reset document', 'Error');
          return EMPTY;
        }),
        switchMap(() => from(this.loadDocument(document.documentId)))
        );
  }
}
